import { Link } from "react-router-dom";
import { header1Styles } from "@consts";
import {
  EXPLORER_LAYER_NOTES,
  EXPLORER_META_CHIPS,
  glossaryHref,
} from "../../content/siteCatalog";
import { GlossaryLink } from "../GlossaryLink";

interface ExplorerHeaderProps {
  generatedLabel: string | null;
  modelVersion?: string;
}

export function ExplorerHeader({
  generatedLabel,
  modelVersion,
}: ExplorerHeaderProps) {
  const chips = [
    ...EXPLORER_META_CHIPS,
    ...(generatedLabel ? [`Maps ${generatedLabel}`] : []),
  ];

  return (
    <header>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="section-label">Explore</p>
          <h1 className={`${header1Styles} mt-2 !mb-0`}>Explore</h1>
        </div>
        <Link
          to="/science"
          className="btn-ghost w-full justify-center !px-4 !py-2.5 text-sm sm:w-auto sm:self-auto"
        >
          Understand the science
        </Link>
      </div>
      <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg">
        Pick an event, then step through each day of a stuck European summer
        pattern — the atmosphere, the CNN&apos;s probability, and where the
        network looked. The maps show a detection;{" "}
        <GlossaryLink termId="blocking">blocking</GlossaryLink>,{" "}
        <GlossaryLink termId="z500">Z500</GlossaryLink>,{" "}
        <GlossaryLink termId="z500-star">Z500*</GlossaryLink>,{" "}
        <GlossaryLink termId="gradcam">Grad-CAM</GlossaryLink>, and the thesis
        scores are defined on the Science page.
      </p>

      <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {EXPLORER_LAYER_NOTES.map((note) => (
          <li key={note.glossaryId}>
            <a
              href={glossaryHref(note.glossaryId)}
              className="panel block h-full px-4 py-3 transition hover:border-info/50"
            >
              <p className="text-sm font-semibold text-ink">{note.label}</p>
              <p className="mt-1 text-sm leading-snug text-muted">{note.short}</p>
              <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-info">
                Definition
              </p>
            </a>
          </li>
        ))}
      </ul>

      <ul className="mt-4 flex flex-wrap gap-2">
        {chips.map((chip) => (
          <li
            key={chip}
            className="rounded-full border border-line/35 bg-surface/80 px-2.5 py-1 text-[11px] font-semibold text-muted"
          >
            {chip}
          </li>
        ))}
      </ul>

      <details className="mt-4 text-sm">
        <summary className="cursor-pointer font-semibold text-ink marker:text-info hover:text-info">
          Source, grid, and published scores
        </summary>
        <p className="mt-2 leading-relaxed text-muted">
          Historical events use <GlossaryLink termId="era5">ERA5</GlossaryLink>;
          2026 uses ERA5T (Copernicus / ECMWF), regridded to the 1° thesis Europe
          grid. Each map is 00:00 UTC; the{" "}
          <GlossaryLink termId="cnn">CNN</GlossaryLink> input is a{" "}
          <GlossaryLink termId="window">7-day stack</GlossaryLink> centred on that
          day
          {modelVersion ? ` (${modelVersion})` : ""}. Thesis{" "}
          <GlossaryLink termId="f1">F1 0.72 / ~78% recall</GlossaryLink> describe{" "}
          <GlossaryLink termId="gtd">GTD</GlossaryLink> detection on 1979–2018 only
          — not 2026. The 2026 values are retrospective blocking detections, not
          weather forecasts.
        </p>
      </details>
    </header>
  );
}
